import { useMutation } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/lib/supabase';
import { nextRouteAfterAuth } from '@/features/auth/lib/password-reset';
import { DEFAULT_POST_AUTH_PATH } from '@/lib/default-landing';

/**
 * Supabase Auth surface for the V3 password-reset flow.
 *
 * These hooks wrap `@supabase/supabase-js` auth calls as TanStack Query
 * mutations (`state.mdc` §3 — no `useEffect`-fetch, no direct client calls
 * from components). They are the building blocks for routing the ~860
 * reset-required migrated users into a "set your password" screen. They are
 * only usable when the Supabase auth surface is configured
 * (`isSupabaseAuthEnabled()`); the legacy Express login remains the default.
 */

export type SupabaseSignInInput = {
  email: string;
  password: string;
};

export type SupabaseSignInResult = {
  session: Session;
  /** Where to navigate next — set-password for reset-required users. */
  redirectTo: string;
};

/**
 * Sign in via the Supabase password grant. On success, computes the next
 * route: reset-required users (`app_metadata.requires_password_reset`) are
 * routed to the set-password screen; everyone else to `fallback`.
 */
export function useSupabaseSignIn(fallback = DEFAULT_POST_AUTH_PATH) {
  return useMutation<SupabaseSignInResult, Error, SupabaseSignInInput>({
    mutationFn: async ({ email, password }) => {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (!data.session) throw new Error('No session returned from sign-in.');
      return { session: data.session, redirectTo: nextRouteAfterAuth(data.session, fallback) };
    },
  });
}

/**
 * Send the Supabase recovery / activation email. This is the path a
 * passwordless reset-required user takes to obtain a session: the link lands
 * them on the set-password screen with a recovery session. Always resolves so
 * the UI can show a neutral "check your email" message without enumerating
 * which addresses exist.
 */
export function useRequestPasswordReset() {
  return useMutation<void, Error, { email: string }>({
    mutationFn: async ({ email }) => {
      const supabase = getSupabaseClient();
      const redirectTo = `${window.location.origin}/v2/set-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;
    },
  });
}

/**
 * Set the authenticated user's password (the set-password screen submit).
 * Requires an active Supabase session (a recovery session from the email link
 * counts). After this succeeds the user has a real password.
 */
export function useSetPassword() {
  return useMutation<void, Error, { password: string }>({
    mutationFn: async ({ password }) => {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    },
  });
}
